import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';
import { useTimezone } from '../contexts/TimezoneContext';

/**
 * Daybreak ritual — passive nudge that surfaces a non-blocking banner
 * (rendered inside TodayView, NOT a modal) when the user hasn't
 * completed today's ritual. The actual modal opens only on explicit
 * user action (banner CTA, nudge-bell quick action, or the dev
 * `window.__polkdesqOpenDaybreak()` hook).
 *
 * Why non-blocking?
 * The old behaviour auto-popped a full-screen modal on every first
 * paint, intercepting pointer events on the topbar / search button
 * (verified by testing agent iteration 136). Users would routinely
 * dismiss it just to reach the rest of the app. A passive banner
 * preserves the prompt without holding the UI hostage.
 *
 * Locally-cached via `polkdesq_daybreak_dismissed_YYYY-MM-DD` so we
 * don't re-nudge after the user has skipped or completed today's
 * ritual, even before the server check returns.
 */
export function useDaybreakRitual() {
  const { todayDateStrInTz } = useTimezone();
  const [daybreakOpen, setDaybreakOpen] = useState(false);
  const [daybreakPending, setDaybreakPending] = useState(false);

  // Dev-only escape hatch: `window.__polkdesqOpenDaybreak()` deterministically
  // opens the modal in any environment, sidestepping the calendar-day +
  // completedToday gating that makes the prompt hard to reproduce in
  // e2e tests. Stripped from production builds.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    if (typeof window === 'undefined') return undefined;
    window.__polkdesqOpenDaybreak = () => setDaybreakOpen(true);
    return () => {
      try {
        delete window.__polkdesqOpenDaybreak;
      } catch (err) {
        // Strict-mode getters or frozen objects in some test
        // environments throw on delete — never user-impacting, but
        // worth a debug breadcrumb (was a silent catch, flagged in
        // code review).
        logger.debug('useDaybreakRitual: failed to clean up dev hook', err?.message);
      }
    };
  }, []);

  useEffect(() => {
    const today = todayDateStrInTz();
    const localKey = `polkdesq_daybreak_dismissed_${today}`;
    if (safeStorage.getString(localKey) === '1') return undefined;
    let cancelled = false;
    api.get('/daybreak/today').then((res) => {
      if (cancelled) return;
      if (!res?.data?.completedToday) {
        // Show the banner — NOT the modal. User chooses when to engage.
        setDaybreakPending(true);
      } else {
        safeStorage.setString(localKey, '1');
      }
    }).catch((err) => {
      logger.warn('Daybreak check failed (banner skipped, non-fatal):', err?.message);
    });
    return () => { cancelled = true; };
  }, [todayDateStrInTz]);

  const openDaybreak = useCallback(() => {
    setDaybreakOpen(true);
  }, []);

  const handleDaybreakClose = useCallback((completed = false) => {
    const today = todayDateStrInTz();
    setDaybreakOpen(false);
    if (completed) {
      // User saved the ritual — clear the banner immediately and
      // mark the day done locally so we don't re-nudge on refresh.
      safeStorage.setString(`polkdesq_daybreak_dismissed_${today}`, '1');
      setDaybreakPending(false);
    }
    // Else: user just closed the modal without completing. Keep the
    // banner so they can come back later in the day.
  }, [todayDateStrInTz]);

  const dismissForToday = useCallback(() => {
    const today = todayDateStrInTz();
    safeStorage.setString(`polkdesq_daybreak_dismissed_${today}`, '1');
    setDaybreakPending(false);
    setDaybreakOpen(false);
    // Persist server-side so hard reloads / other devices also stay
    // dismissed for the rest of today. Best-effort — local flag wins
    // if the request fails.
    api.post('/daybreak/skip').catch((err) => {
      logger.warn('Daybreak skip not persisted server-side (local flag still holds):', err?.message);
    });
  }, [todayDateStrInTz]);

  return {
    daybreakOpen,
    daybreakPending,
    openDaybreak,
    handleDaybreakClose,
    dismissForToday,
  };
}
